/**
 * Dialog Component - Reusable modal overlay
 */

import { useEffect } from 'preact/hooks';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  children: preact.ComponentChildren;
  title?: string;
}

export function Dialog({ isOpen, onClose, children, title }: DialogProps) {
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    // Prevent body scroll when dialog is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        // Close when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Backdrop */}
      <div class="absolute inset-0 bg-black/60 backdrop-blur-xl" />

      {/* Dialog Content */}
      <div
        class="col relative w-full max-w-md h-full max-h-[90vh] bg-neutral-900 rounded-3xl border border-neutral-800 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        {title ? (
          <div class="flex items-center justify-between p-6 py-4 border-b border-neutral-700">
            <h2 class="text-xl font-semibold text-white">{title}</h2>
            <button
              onClick={onClose}
              class="text-neutral-400 hover:text-white transition-colors p-1 rounded hover:bg-neutral-700"
              aria-label="Close dialog"
            >
              <svg
                class="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        ) : <div class="absolute top-6 right-6">
          <button
            onClick={onClose}
            class="text-neutral-400 hover:text-white transition-colors p-1 rounded bg-neutral-900 hover:bg-neutral-700"
            aria-label="Close dialog"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>}

        {/* Content */}
        <div class="flex-1 overflow-y-auto col">
          {children}
        </div>
      </div>
    </div>
  );
}
